import { Link } from "@tanstack/react-router"
import {
  BarChart3,
  CreditCard,
  Flag,
  House,
  LifeBuoy,
  MessageSquareText,
  Route,
  UserRound,
} from "lucide-react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import type { LearnerPage } from "../lib/learner-access"

const learningDestinations = [
  { page: "home", icon: House, key: "home", to: "/" },
  { page: "mastery", icon: Flag, key: "mastery", to: "/mastery" },
  { page: "path", icon: Route, key: "path", to: "/path" },
  { page: "analytics", icon: BarChart3, key: "analytics", to: "/analytics" },
] as const

const accountDestinations = [
  { page: "account", icon: UserRound, key: "account", to: "/account" },
  { page: "billing", icon: CreditCard, key: "billing", to: "/billing" },
  { page: "support", icon: LifeBuoy, key: "support", to: "/support" },
  { page: "feedback", icon: MessageSquareText, key: "feedback", to: "/feedback" },
] as const

export function LearnerShell({
  children,
  guardianOnly,
  name,
  page,
}: {
  children: React.ReactNode
  guardianOnly: boolean
  name: string
  page: LearnerPage
}) {
  const { t } = useTranslation()
  const accountContext = guardianOnly || isAccountPage(page)

  return (
    <div className={cn("app-shell", guardianOnly && "guardian-only-shell")}>
      <a className="skip-link" href="#content">
        {t("a11y.skipToContent")}
      </a>
      <header className="app-topbar">
        <Link
          className="wordmark"
          to={guardianOnly ? "/account" : "/"}
          aria-label={guardianOnly ? t("a11y.guardianAccountHome") : t("a11y.homeLabel")}
        >
          TutorLite<span>.</span>
        </Link>
        {accountContext ? (
          <AccountNavigation className="desktop-account-nav" page={page} />
        ) : (
          <nav className="desktop-primary-nav" aria-label={t("a11y.learningNavigation")}>
            {learningDestinations.map((destination) => (
              <DesktopLink
                active={page === destination.page}
                icon={destination.icon}
                key={destination.page}
                label={t(`nav.${destination.key}`)}
                to={destination.to}
              />
            ))}
          </nav>
        )}
        <ProfileMenu name={name} page={page} />
      </header>
      <main id="content" className="page-wrap" tabIndex={-1}>
        {children}
      </main>
      {accountContext ? (
        <AccountNavigation className="mobile-account-nav" page={page} mobile />
      ) : (
        <nav className="mobile-primary-nav" aria-label={t("a11y.learningNavigation")}>
          {learningDestinations.map((destination) => (
            <MobileLink
              active={page === destination.page}
              icon={destination.icon}
              key={destination.page}
              label={t(`nav.${destination.key}`)}
              to={destination.to}
            />
          ))}
        </nav>
      )}
    </div>
  )
}

export function profileIdentity(name: string) {
  const firstWord = name.trim().split(/\s+/u)[0] ?? ""
  const segment = firstWord
    ? Array.from(new Intl.Segmenter("en", { granularity: "grapheme" }).segment(firstWord))[0]
        ?.segment
    : undefined
  const upperSegment = segment?.toUpperCase()
  const initial = upperSegment
    ? Array.from(new Intl.Segmenter("en", { granularity: "grapheme" }).segment(upperSegment))[0]
        ?.segment || "?"
    : "?"
  const latinInitial = initial
    .normalize("NFD")
    .replace(/\p{Mark}/gu, "")
    .charAt(0)
  const latinIndex = /^[A-Z]$/.test(latinInitial) ? latinInitial.charCodeAt(0) - 65 : undefined
  const hash = Array.from(initial).reduce(
    (value, character) => (value * 31 + (character.codePointAt(0) ?? 0)) >>> 0,
    0,
  )
  return {
    initial,
    colorClass: `profile-color-${((latinIndex ?? hash) % 6) + 1}`,
  }
}

function ProfileMenu({ name, page }: { name: string; page: LearnerPage }) {
  const { t } = useTranslation()
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const identity = profileIdentity(name)

  useEffect(() => {
    const details = detailsRef.current
    if (!details) return

    const closeOutside = (event: PointerEvent) => {
      if (details.open && !details.contains(event.target as Node)) details.open = false
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !details.open) return
      details.open = false
      details.querySelector<HTMLElement>("summary")?.focus()
    }
    document.addEventListener("pointerdown", closeOutside)
    document.addEventListener("keydown", closeOnEscape)
    return () => {
      document.removeEventListener("pointerdown", closeOutside)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [])

  useEffect(() => {
    if (detailsRef.current) detailsRef.current.open = false
  }, [page])

  return (
    <details className="profile-menu" ref={detailsRef}>
      <summary
        className="profile-trigger"
        aria-label={t("a11y.openAccountMenu", { name })}
        title={t("a11y.openAccountMenu", { name })}
      >
        <span className={cn("profile-avatar", identity.colorClass)} aria-hidden="true">
          {identity.initial}
        </span>
      </summary>
      <nav className="profile-menu-panel" aria-label={t("a11y.accountNavigation")}>
        {accountDestinations.map((destination) => {
          const Icon = destination.icon
          const active = page === destination.page
          return (
            <Link
              aria-current={active ? "page" : undefined}
              className={active ? "profile-menu-link-active" : undefined}
              key={destination.page}
              onClick={() => {
                if (detailsRef.current) detailsRef.current.open = false
              }}
              to={destination.to}
            >
              <Icon aria-hidden="true" />
              {t(`nav.${destination.key}`)}
            </Link>
          )
        })}
      </nav>
    </details>
  )
}

function AccountNavigation({
  className,
  mobile = false,
  page,
}: {
  className: string
  mobile?: boolean
  page: LearnerPage
}) {
  const { t } = useTranslation()
  return (
    <nav className={className} aria-label={t("a11y.accountNavigation")}>
      {accountDestinations.map((destination) => {
        const active = page === destination.page
        return mobile ? (
          <MobileLink
            active={active}
            icon={destination.icon}
            key={destination.page}
            label={t(`nav.${destination.key}`)}
            to={destination.to}
          />
        ) : (
          <Link
            aria-current={active ? "page" : undefined}
            className={active ? "account-nav-link-active" : undefined}
            key={destination.page}
            to={destination.to}
          >
            {t(`nav.${destination.key}`)}
          </Link>
        )
      })}
    </nav>
  )
}

function isAccountPage(page: LearnerPage) {
  return page === "account" || page === "billing" || page === "feedback" || page === "support"
}

function DesktopLink({
  active,
  icon: Icon,
  label,
  to,
}: {
  active: boolean
  icon: typeof House
  label: string
  to: "/" | "/analytics" | "/mastery" | "/path"
}) {
  return (
    <Link
      className={cn(
        buttonVariants({ variant: active ? "outline" : "ghost", size: "sm" }),
        "nav-link",
      )}
      aria-current={active ? "page" : undefined}
      to={to}
    >
      <Icon data-icon="inline-start" aria-hidden="true" />
      {label}
    </Link>
  )
}

function MobileLink({
  active,
  icon: Icon,
  label,
  to,
}: {
  active: boolean
  icon: typeof House
  label: string
  to: "/" | "/account" | "/analytics" | "/billing" | "/feedback" | "/mastery" | "/path" | "/support"
}) {
  return (
    <Link
      className={cn("mobile-nav-link", active && "mobile-nav-link-active")}
      aria-current={active ? "page" : undefined}
      to={to}
    >
      <Icon className="mobile-nav-icon" aria-hidden="true" />
      <span>{label}</span>
    </Link>
  )
}
